import { useEffect, useState, useRef, useCallback } from 'react'
import { Tree, ITreeNode } from '@blueprintjs/core'
import { cloneDeep, isEmpty } from 'lodash'
import { IPortfolioRegion } from '@api'
import { mapLocationTreeContents, forEachNode, LocationTreeNodeT, forEachParenNode } from './utils'
import makeDisableEachNodeBySelectedFloors from './utils/makeDisableEachNodeBySelectedFloors'
import makeSelectEachNodeByFloorWorkdayTime from './utils/makeSelectEachNodeByFloorWorkdayTime'

type UseLocationTreeContentsArgs = {
  regions: IPortfolioRegion[]
  selectedFloorIds: string[]
  disabledFloorIds: string[]
}

const useLocationTreeContents = ({
  regions,
  selectedFloorIds,
  disabledFloorIds,
}: UseLocationTreeContentsArgs) => {
  const [locationTreeNodes, setLocationTreeNodes] = useState<LocationTreeNodeT[]>([])
  const isLocationTreeNodesInitializedRef = useRef(false)

  const _setTreeNodesBySelectedFloorIds = useCallback(
    (selectedFloorIds: string[]) => {
      const locationTreeNodes = mapLocationTreeContents(regions)
      const selectEachNodeByWorkdayTime = makeSelectEachNodeByFloorWorkdayTime(selectedFloorIds)
      const disabledEachNodeByDisabledFloorIds =
        makeDisableEachNodeBySelectedFloors(disabledFloorIds)
      forEachNode((node: LocationTreeNodeT) => {
        selectEachNodeByWorkdayTime(node)
        disabledEachNodeByDisabledFloorIds(node)
      }, locationTreeNodes)

      setLocationTreeNodes(locationTreeNodes)
    },
    [regions]
  )

  // it's necessary to mutate the state
  const clonedLocationTreeNodes = cloneDeep(locationTreeNodes)

  const setLocationContents = (
    nodePath: number[],
    callback: (node: ITreeNode) => void,
    forEachParentCallback?: (node: ITreeNode) => void,
    selectedLocationTreeNodes?: LocationTreeNodeT[]
  ): void => {
    // it mutates the clonedLocationTreeNodes object
    const updatedTree = selectedLocationTreeNodes ?? clonedLocationTreeNodes
    callback(Tree.nodeFromPath(nodePath, updatedTree))

    if (forEachParentCallback) {
      forEachParenNode(nodePath, forEachParentCallback, updatedTree)
    }

    setLocationTreeNodes(cloneDeep(updatedTree))
  }

  const setLocationContentsForEachNode = (
    callback: (
      node: LocationTreeNodeT,
      parentCollection: LocationTreeNodeT[],
      index: number
    ) => void
  ): void => {
    // it mutates the clonedLocationTreeNodes object
    forEachNode(callback, clonedLocationTreeNodes)

    setLocationTreeNodes(clonedLocationTreeNodes)
  }

  const resetLocationTreeNodes = () => {
    const selectEachNodeByWorkdayTime = makeSelectEachNodeByFloorWorkdayTime(selectedFloorIds)
    const disabledEachNodeByDisabledFloorIds = makeDisableEachNodeBySelectedFloors(disabledFloorIds)
    forEachNode((node: LocationTreeNodeT) => {
      selectEachNodeByWorkdayTime(node)
      disabledEachNodeByDisabledFloorIds(node)
    }, clonedLocationTreeNodes)
    setLocationTreeNodes(clonedLocationTreeNodes)
  }

  // init locationTreeNodes states
  useEffect(() => {
    if (isLocationTreeNodesInitializedRef.current || isEmpty(regions)) return

    _setTreeNodesBySelectedFloorIds(selectedFloorIds)
    isLocationTreeNodesInitializedRef.current = true
  }, [
    regions,
    _setTreeNodesBySelectedFloorIds,
    selectedFloorIds,
    isLocationTreeNodesInitializedRef,
  ])

  useEffect(() => {
    if (!isEmpty(regions)) {
      _setTreeNodesBySelectedFloorIds(selectedFloorIds)
    }
  }, [regions, _setTreeNodesBySelectedFloorIds, selectedFloorIds])

  return {
    locationTreeNodes,
    setLocationContents,
    setLocationContentsForEachNode,
    setLocationTreeNodes,
    resetLocationTreeNodes,
  }
}

export default useLocationTreeContents
